import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  user-select: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: ${({ objectFit }) => objectFit};
    opacity: 0;
    transition: opacity 0.5s ease;
  }

  img.loaded {
    opacity: 1;
  }
`;

// Throttle helper to reduce ResizeObserver and resize events frequency
const throttle = (fn, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

const Image = ({
  src,
  alt = 'image',
  loading = 'lazy',
  objectFit = 'cover',
  widths = [860, 1080, 1440, 1920, 2160], // Default responsive breakpoints
}) => {
  const [containerWidth, setContainerWidth] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [isLoaded, setIsLoaded] = useState(false);
  const wrapperRef = useRef(null);

  // Strip extension and trailing -### suffix to get base name
  const base = src.replace(/\.\w+$/, '').replace(/-\d+$/, '');
  const srcSetWebp = widths.map((w) => `${base}-${w}.webp ${w}w`).join(', ');

  // Throttled container width tracking
  const updateWidth = useCallback(
    throttle((width) => {
      setContainerWidth(width);
    }, 200),
    [],
  );

  useEffect(() => {
    if (!wrapperRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateWidth(Math.round(entry.contentRect.width));
      }
    });
    observer.observe(wrapperRef.current);

    const handleResize = throttle(
      () => setViewportWidth(window.innerWidth),
      200,
    );
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [updateWidth]);

  // Auto-detect scale for mobile sizes (clamped to 100vw max)
  const mobileScale =
    containerWidth && viewportWidth && viewportWidth > 0
      ? Math.min(containerWidth / viewportWidth, 1)
      : 1;

  const mobileSize = `${Math.round(mobileScale * 100)}vw`;

  // Build sizes: dynamic mobile scale, container width for desktop
  const sizes =
    containerWidth !== null
      ? `(max-width: 768px) ${mobileSize}, ${containerWidth}px`
      : '100vw';

  return (
    <Wrapper objectFit={objectFit} ref={wrapperRef}>
      <picture>
        <source srcSet={srcSetWebp} sizes={sizes} type="image/webp" />
        <img
          src={src}
          srcSet={srcSetWebp}
          sizes={sizes}
          alt={alt}
          loading={loading}
          decoding="async"
          className={isLoaded ? 'loaded' : ''}
          onLoad={() => setIsLoaded(true)}
        />
      </picture>
    </Wrapper>
  );
};

Image.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  loading: PropTypes.oneOf(['lazy', 'eager']),
  objectFit: PropTypes.oneOf([
    'fill',
    'contain',
    'cover',
    'none',
    'scale-down',
  ]),
  widths: PropTypes.arrayOf(PropTypes.number),
};
export default Image;
